// P1 · ¿Qué móvil llevás hoy? (documento 07). Al elegir se descarga el paquete del día.
import { Boton, Chip, Icono, Tarjeta, type TonoChip, useToast } from '@reparto/ui';
import { formatoFecha, formatoGs, hoyISO } from '@reparto/shared/nucleo';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ErrorApi, SinRed } from '../datos/api';
import { descargarPaquete, listarMoviles } from '../datos/paquete';
import type { MovilLista } from '../datos/tipos';
import { pantallaSegun } from '../datos/vista';
import { EsqueletoMoviles } from '../componentes/Esqueletos';
import { Marco, Vacio } from '../componentes/Marco';
import { diaDeCarga } from '../componentes/formato';
import { cambiarMovil } from '../servicios/tiempo-real';
import { gps } from '../servicios/gps';

const mayuscula = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const ESTADO: Record<string, { texto: string; tono: TonoChip }> = {
  sin: { texto: 'Por cargar', tono: 'neutro' },
  armada: { texto: 'Cargado', tono: 'aviso' },
  en_curso: { texto: 'En ruta', tono: 'info' },
  cerrada: { texto: 'Cerrado', tono: 'oscuro' },
};

export function Movil() {
  const navegar = useNavigate();
  const { mostrar } = useToast();
  const [moviles, setMoviles] = useState<MovilLista[] | null>(null);
  const [desdeCache, setDesdeCache] = useState(false);
  const [eligiendo, setEligiendo] = useState<string | null>(null);

  const cargar = () =>
    listarMoviles()
      .then((r) => {
        setMoviles(r.datos);
        setDesdeCache(r.desdeCache);
      })
      .catch((e) => {
        setMoviles([]);
        mostrar(e instanceof ErrorApi ? e.message : 'No se pudo cargar la lista', { tipo: 'error' });
      });

  useEffect(() => {
    void cargar();
  }, []);

  /** `fecha`: hoy, o el próximo día con pedidos para cargar desde la tarde anterior. */
  async function elegir(m: MovilLista, fecha = hoyISO()) {
    setEligiendo(m.id);
    try {
      // Se pide permiso de ubicación al elegir, con la explicación en pantalla (documento 07, GPS).
      void gps.actual();
      const p = await descargarPaquete(m.id, fecha);
      cambiarMovil(m.id);
      if (p.aviso_otro_chofer)
        mostrar(`Esta ruta la empezó ${p.aviso_otro_chofer.nombre}. Si seguís, queda registrado a tu nombre.`, {
          tipo: 'info',
          duracionMs: 6000,
        });
      navegar(pantallaSegun(p), { replace: true });
    } catch (e) {
      mostrar(
        e instanceof SinRed
          ? 'Sin señal: para descargar el día necesitás conexión.'
          : e instanceof ErrorApi
            ? e.message
            : 'No se pudo descargar el día',
        { tipo: 'error' },
      );
    } finally {
      setEligiendo(null);
    }
  }

  return (
    <Marco titulo="¿Qué móvil llevás hoy?" subtitulo={formatoFecha(hoyISO())}>
      <div className="space-y-3 p-4">
        <div className="flex gap-2 rounded-2xl bg-primary-50 p-3 text-sm text-primary-900 dark:bg-primary-900/30 dark:text-primary-100">
          <Icono nombre="my_location" className="shrink-0" />
          <span>Mientras estés en ruta, la app comparte tu ubicación con el depósito. Solo durante la ruta.</span>
        </div>
        {desdeCache ? (
          <div className="text-xs text-amber-700 dark:text-amber-300">
            Sin señal: estos datos son de la última vez que te conectaste.
          </div>
        ) : null}
        {moviles === null ? <EsqueletoMoviles /> : null}
        {moviles?.length === 0 ? (
          <Vacio
            icono="local_shipping"
            titulo="No hay móviles"
            texto="Pedile al encargado que cargue los móviles de la empresa."
          >
            <Boton variante="secundario" bloque icono="refresh" onClick={() => void cargar()}>
              Reintentar
            </Boton>
          </Vacio>
        ) : null}
        {moviles?.map((m) => {
          const e = ESTADO[m.hoy.ruta_estado ?? 'sin']!;
          // Hoy sin pedidos y sin ruta: lo que importa es lo próximo, que se puede cargar desde ahora.
          const proximo = m.proximo && !m.hoy.ruta_estado ? m.proximo : null;
          return (
            <Tarjeta key={m.id} resaltada={m.es_mio} relleno="none" className="overflow-hidden">
              <button
                type="button"
                onClick={() => void elegir(m)}
                disabled={!!eligiendo}
                className="flex w-full items-center gap-3 p-4 text-left active:bg-stone-50 dark:active:bg-stone-800"
              >
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200">
                  <Icono
                    nombre={eligiendo === m.id ? 'progress_activity' : 'local_shipping'}
                    className={eligiendo === m.id ? 'animate-spin' : ''}
                  />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate font-medium">{m.nombre}</span>
                    {m.es_mio ? <Chip tono="exito">Tu móvil</Chip> : null}
                  </span>
                  <span className="block truncate text-xs text-stone-500 dark:text-stone-400">
                    {m.chofer_default_nombre ? `Chofer ${m.chofer_default_nombre} · ` : ''}
                    {m.hoy.pedidos ? `Hoy ${m.hoy.pedidos} pedidos · ${m.hoy.bultos} bultos` : 'Hoy sin pedidos'}
                    {m.hoy.a_cobrar ? ` · cobrar ${formatoGs(m.hoy.a_cobrar)}` : ''}
                  </span>
                </span>
                <Chip tono={e.tono}>{e.texto}</Chip>
              </button>
              {proximo ? (
                <button
                  type="button"
                  onClick={() => void elegir(m, proximo.fecha)}
                  disabled={!!eligiendo}
                  className="flex w-full items-center gap-2 border-t border-stone-200 bg-stone-50 px-4 py-3 text-left text-sm text-primary-800 active:bg-stone-100 dark:border-stone-700 dark:bg-stone-900/40 dark:text-primary-200"
                >
                  <Icono nombre="event_upcoming" tamano={20} className="shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="font-semibold">{mayuscula(diaDeCarga(proximo.fecha))}</span>:{' '}
                    {proximo.pedidos} {proximo.pedidos === 1 ? 'pedido' : 'pedidos'} · {proximo.bultos} bultos
                  </span>
                  <span className="shrink-0 font-semibold">Cargar</span>
                  <Icono nombre="chevron_right" tamano={20} className="shrink-0" />
                </button>
              ) : null}
            </Tarjeta>
          );
        })}
      </div>
    </Marco>
  );
}
